import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, DatePicker, Button, Statistic, Table, Tabs, message, Space } from 'antd';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { statisticsService } from '../../services';
import dayjs from 'dayjs';
import './Statistics.css';

const Statistics = () => {
    const [currentUser, setCurrentUser] = useState(null);
    const [accessDenied, setAccessDenied] = useState(false);

    useEffect(() => {
        async function fetchUser() {
            try {
                const res = await import('../../services').then(m => m.authService.getCurrentUser());
                if (res.data && res.data.code === 200) {
                    setCurrentUser(res.data.data);
                    const role = res.data.data.role;
                    // 仅系统管理员、管理员、高级用户可访问
                    if (!['admin', 'manager', 'advanced_user'].includes(role)) {
                        setAccessDenied(true);
                    }
                } else {
                    setAccessDenied(true);
                }
            } catch {
                setAccessDenied(true);
            }
        }
        fetchUser();
    }, []);
    const [maximizedKey, setMaximizedKey] = useState(null);

    // 最大化按钮渲染
    const renderMaxBtn = (key) => (
        <Button type="text" size="small" style={{float: 'right'}} onClick={() => setMaximizedKey(key)}>
            <span role="img" aria-label="最大化" style={{fontSize: 18}}>🗖</span>
        </Button>
    );

    // 关闭最大化
    const handleCloseMax = () => setMaximizedKey(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({});
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [stats, setStats] = useState({
        total: 0,
        trendDaily: [],
        byMonth: [],
        byWeather: [],
        byRoadSurface: [],
        topInvestigators: [],
        topDrawers: []
    });

    const fetchStatistics = useCallback(async () => {
        try {
            setLoading(true);
            const [
                totalRes,
                trendRes,
                monthRes,
                weatherRes,
                roadRes,
                investigatorsRes,
                drawersRes
            ] = await Promise.all([
                statisticsService.getTotalAccidents(filters),
                statisticsService.getTrendByDay(filters),
                statisticsService.getStatisticsByMonth(filters),
                statisticsService.getStatisticsByWeather(filters),
                statisticsService.getStatisticsByRoadSurface(filters),
                statisticsService.getTopInvestigators(filters),
                statisticsService.getTopDrawers(filters)
            ]);

            setStats({
                total: totalRes.data.data.total,
                trendDaily: trendRes.data.data,
                byMonth: monthRes.data.data,
                byWeather: weatherRes.data.data,
                byRoadSurface: roadRes.data.data,
                topInvestigators: investigatorsRes.data.data,
                topDrawers: drawersRes.data.data
            });
        } catch (error) {
            message.error('获取统计数据失败');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchStatistics();
    }, [fetchStatistics]);

    const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#a4de6c', '#d084d0'];

    // 快捷时间选择
    const handleQuickDateRange = (days) => {
        const end = dayjs();
        const start = dayjs().subtract(days, 'day');
        // 只更新日期状态，不触发查询
        setStartDate(start);
        setEndDate(end);
    };

    const handleDateChange = (field, date) => {
        // 只更新本地日期状态，不触发查询
        if (field === 'startTime') {
            setStartDate(date);
        } else {
            setEndDate(date);
        }
    };

    const handleManualSearch = () => {
        // 手动查询时，根据当前日期状态更新 filters
        const newFilters = {};
        if (startDate) {
            newFilters.startTime = startDate.format('YYYY-MM-DD');
        }
        if (endDate) {
            newFilters.endTime = endDate.format('YYYY-MM-DD');
        }
        setFilters(newFilters);
    };

    // 初次进入默认选择最近7天，统一全局筛选
    useEffect(() => {
        if (!startDate && !endDate) {
            handleQuickDateRange(7);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Tabs items for Antd v5+ (avoid deprecated Tabs.TabPane)
    const monthItems = [
        {
            key: '1',
            label: '柱状图',
            children: (
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={stats.byMonth}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                            dataKey="month"
                            tickFormatter={(value) => value ? value.replace(/-/g, '年') + '月' : value}
                        />
                        <YAxis />
                        <Tooltip 
                            formatter={(value, name) => {
                                if (name === 'count') return [`共${value}起`, '数量'];
                                return [value, name];
                            }}
                            labelFormatter={(label) => label ? label.replace(/-/g, '年') + '月' : label}
                        />
                        <Bar dataKey="count" fill="#8884d8" />
                    </BarChart>
                </ResponsiveContainer>
            )
        },
        {
            key: '2',
            label: '表格',
            children: (
                <Table
                    columns={[
                        { 
                            title: '月份', 
                            dataIndex: 'month', 
                            key: 'month',
                            render: (text) => text ? text.replace(/-/g, '年') + '月' : text
                        },
                        { 
                            title: '数量', 
                            dataIndex: 'count', 
                            key: 'count',
                            render: (count) => `共${count}起`
                        }
                    ]}
                    dataSource={stats.byMonth}
                    pagination={false}
                    size="small"
                />
            )
        }
    ];

    const weatherItems = [
        {
            key: '1',
            label: '柱状图',
            children: (
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={stats.byWeather}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="weather" />
                        <YAxis />
                        <Tooltip 
                            formatter={(value, name) => {
                                if (name === 'count') return [`共${value}起`, '数量'];
                                return [value, name];
                            }}
                        />
                        <Bar dataKey="count" fill="#ffc658" />
                    </BarChart>
                </ResponsiveContainer>
            )
        },
        {
            key: '2',
            label: '饼形图',
            children: (
                <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                        <Pie
                            data={stats.byWeather}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ weather, count }) => `${weather}: 共${count}起`}
                            outerRadius={120}
                            fill="#8884d8"
                            dataKey="count"
                        >
                            {stats.byWeather.map((_, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value) => `共${value}起`} />
                    </PieChart>
                </ResponsiveContainer>
            )
        },
        {
            key: '3',
            label: '表格',
            children: (
                <Table
                    columns={[
                        { title: '天气', dataIndex: 'weather', key: 'weather' },
                        { 
                            title: '数量', 
                            dataIndex: 'count', 
                            key: 'count',
                            render: (count) => `共${count}起`
                        }
                    ]}
                    dataSource={stats.byWeather}
                    pagination={false}
                    size="small"
                />
            )
        }
    ];

    const roadSurfaceItems = [
        {
            key: '1',
            label: '柱状图',
            children: (
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={stats.byRoadSurface}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="road_surface" />
                        <YAxis />
                        <Tooltip 
                            formatter={(value, name) => {
                                if (name === 'count') return [`共${value}起`, '数量'];
                                return [value, name];
                            }}
                        />
                        <Bar dataKey="count" fill="#ff7c7c" />
                    </BarChart>
                </ResponsiveContainer>
            )
        },
        {
            key: '2',
            label: '饼形图',
            children: (
                <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                        <Pie
                            data={stats.byRoadSurface}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ road_surface, count }) => `${road_surface}: 共${count}起`}
                            outerRadius={120}
                            fill="#8884d8"
                            dataKey="count"
                        >
                            {stats.byRoadSurface.map((_, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value) => `共${value}起`} />
                    </PieChart>
                </ResponsiveContainer>
            )
        },
        {
            key: '3',
            label: '表格',
            children: (
                <Table
                    columns={[
                        { title: '路面性质', dataIndex: 'road_surface', key: 'road_surface' },
                        { 
                            title: '数量', 
                            dataIndex: 'count', 
                            key: 'count',
                            render: (count) => `共${count}起`
                        }
                    ]}
                    dataSource={stats.byRoadSurface}
                    pagination={false}
                    size="small"
                />
            )
        }
    ];

    const investigatorsItems = [
        {
            key: '1',
            label: '柱状图',
            children: (
                <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={stats.topInvestigators} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                                type="number" 
                                allowDecimals={false}
                                tickFormatter={v => v.toFixed(0)}
                            />
                            <YAxis dataKey="investigator" type="category" width={80} />
                            <Tooltip 
                                formatter={(value, name) => {
                                    if (name === 'count') return [`共${value}起`, '数量'];
                                    return [value, name];
                                }}
                            />
                            <Bar dataKey="count" fill="#a4de6c" />
                        </BarChart>
                </ResponsiveContainer>
            )
        },
        {
            key: '2',
            label: '饼形图',
            children: (
                <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                        <Pie
                            data={stats.topInvestigators}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ investigator, count }) => `${investigator}: 共${count}起`}
                            outerRadius={120}
                            fill="#8884d8"
                            dataKey="count"
                        >
                            {stats.topInvestigators.map((_, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value) => `共${value}起`} />
                    </PieChart>
                </ResponsiveContainer>
            )
        },
        {
            key: '3',
            label: '表格',
            children: (
                <Table
                    columns={[
                        { title: '勘查员', dataIndex: 'investigator', key: 'investigator' },
                        { 
                            title: '数量', 
                            dataIndex: 'count', 
                            key: 'count',
                            render: (count) => `共${count}起`
                        }
                    ]}
                    dataSource={stats.topInvestigators}
                    pagination={false}
                    size="small"
                />
            )
        }
    ];

    const drawersItems = [
        {
            key: '1',
            label: '柱状图',
            children: (
                <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={stats.topDrawers} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                                type="number" 
                                allowDecimals={false}
                                tickFormatter={v => v.toFixed(0)}
                            />
                            <YAxis dataKey="drawer" type="category" width={80} />
                            <Tooltip 
                                formatter={(value, name) => {
                                    if (name === 'count') return [`共${value}起`, '数量'];
                                    return [value, name];
                                }}
                            />
                            <Bar dataKey="count" fill="#9c88ff" />
                        </BarChart>
                </ResponsiveContainer>
            )
        },
        {
            key: '2',
            label: '饼形图',
            children: (
                <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                        <Pie
                            data={stats.topDrawers}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ drawer, count }) => `${drawer}: 共${count}起`}
                            outerRadius={120}
                            fill="#8884d8"
                            dataKey="count"
                        >
                            {stats.topDrawers.map((_, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value) => `共${value}起`} />
                    </PieChart>
                </ResponsiveContainer>
            )
        },
        {
            key: '3',
            label: '表格',
            children: (
                <Table
                    columns={[
                        { title: '绘图员', dataIndex: 'drawer', key: 'drawer' },
                        { 
                            title: '数量', 
                            dataIndex: 'count', 
                            key: 'count',
                            render: (count) => `共${count}起`
                        }
                    ]}
                    dataSource={stats.topDrawers}
                    pagination={false}
                    size="small"
                />
            )
        }
    ];

    const trendItems = [
        {
            key: '1',
            label: '折线图',
            children: (
                <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={stats.trendDaily}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                            dataKey="date"
                            tickFormatter={(value) => value ? dayjs(value).format('MM月DD日') : value}
                        />
                        <YAxis />
                        <Tooltip 
                            formatter={(value, name) => {
                                if (name === 'count') return [`共${value}起`, '数量'];
                                return [value, name];
                            }}
                            labelFormatter={(label) => label ? dayjs(label).format('YYYY年MM月DD日') : label}
                        />
                        <Legend formatter={(value) => value === 'count' ? '事故数量' : value} />
                        <Line 
                            type="monotone" 
                            dataKey="count" 
                            stroke="#8884d8" 
                            strokeWidth={2}
                            dot={{ fill: '#8884d8', r: 4 }}
                            activeDot={{ r: 6 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            )
        },
        {
            key: '2',
            label: '表格',
            children: (
                <Table
                    columns={[
                        { 
                            title: '日期', 
                            dataIndex: 'date', 
                            key: 'date',
                            render: (text) => text ? dayjs(text).format('YYYY年MM月DD日') : text
                        },
                        { 
                            title: '数量', 
                            dataIndex: 'count', 
                            key: 'count',
                            render: (count) => `共${count}起`
                        }
                    ]}
                    dataSource={stats.trendDaily}
                    pagination={false}
                    size="small"
                />
            )
        }
    ];

    if (accessDenied) {
        return (
            <div style={{textAlign: 'center', padding: '80px 0'}}>
                <Card style={{maxWidth: 480, margin: 'auto'}}>
                    <h2>无权限访问事故统计</h2>
                    <p>只有系统管理员、管理员、高级用户可访问此功能。</p>
                </Card>
            </div>
        );
    }
    // ...existing code...
    return (
        <div className="statistics">
            <Card title="事故统计" className="stats-card">
                {/* 筛选条件 */}
                <Space style={{ marginBottom: 24 }} wrap>
                    <DatePicker
                        placeholder="事故开始日期"
                        value={startDate}
                        format="YYYY年MM月DD日"
                        onChange={(date) => handleDateChange('startTime', date)}
                    />
                    <DatePicker
                        placeholder="事故结束日期"
                        value={endDate}
                        format="YYYY年MM月DD日"
                        onChange={(date) => handleDateChange('endTime', date)}
                    />
                    <Button onClick={() => handleQuickDateRange(7)}>最近7天</Button>
                    <Button onClick={() => handleQuickDateRange(30)}>最近1月</Button>
                    <Button onClick={() => handleQuickDateRange(90)}>最近3月</Button>
                    <Button onClick={() => handleQuickDateRange(180)}>最近半年</Button>
                    <Button onClick={() => handleQuickDateRange(365)}>最近1年</Button>
                    <Button type="primary" onClick={handleManualSearch} loading={loading}>
                        统计
                    </Button>
                </Space>

                {/* 总事故数 */}
                <Row gutter={24} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={8}>
                        <Statistic title="总事故数" value={stats.total} />
                    </Col>
                </Row>

                {/* 统计图2列布局 */}
                {maximizedKey ? (
                    <div style={{
                        zIndex: 1000,
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        background: 'rgba(40,48,72,0.96)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <Card
                            type="inner"
                            title={
                                <span>
                                    {maximizedKey === 'trend' && '按天趋势'}
                                    {maximizedKey === 'month' && '按月份统计'}
                                    {maximizedKey === 'weather' && '按天气统计'}
                                    {maximizedKey === 'road' && '按路面性质统计'}
                                    {maximizedKey === 'investigator' && '勘查员Top 10'}
                                    {maximizedKey === 'drawer' && '绘图员Top 10'}
                                    <Button type="text" size="small" style={{float: 'right'}} onClick={handleCloseMax}>
                                        <span role="img" aria-label="关闭" style={{fontSize: 18}}>✖</span>
                                    </Button>
                                </span>
                            }
                            style={{
                                width: '900px',
                                maxWidth: '96vw',
                                height: '600px',
                                maxHeight: '90vh',
                                margin: 'auto',
                                background: '#f6f8fa',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                                borderRadius: 12,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                            bodyStyle={{height: 'calc(100% - 48px)', overflow: 'auto', padding: 24}}
                        >
                            <div style={{width: '100%', height: '100%'}}>
                                {maximizedKey === 'trend' && <Tabs defaultActiveKey="1" items={trendItems} />}
                                {maximizedKey === 'month' && <Tabs defaultActiveKey="1" items={monthItems} />}
                                {maximizedKey === 'weather' && <Tabs defaultActiveKey="1" items={weatherItems} />}
                                {maximizedKey === 'road' && <Tabs defaultActiveKey="1" items={roadSurfaceItems} />}
                                {maximizedKey === 'investigator' && <Tabs defaultActiveKey="1" items={investigatorsItems} />}
                                {maximizedKey === 'drawer' && <Tabs defaultActiveKey="1" items={drawersItems} />}
                            </div>
                        </Card>
                    </div>
                ) : (
                <Row gutter={[24, 24]}>
                    <Col xs={24} md={8} style={{marginBottom: 24}}>
                        <Card type="inner" title={<span>按天趋势{renderMaxBtn('trend')}</span>} className="inner-card">
                            <Tabs defaultActiveKey="1" items={trendItems} />
                        </Card>
                    </Col>
                    <Col xs={24} md={8} style={{marginBottom: 24}}>
                        <Card type="inner" title={<span>按月份统计{renderMaxBtn('month')}</span>} className="inner-card">
                            <Tabs defaultActiveKey="1" items={monthItems} />
                        </Card>
                    </Col>
                    <Col xs={24} md={8} style={{marginBottom: 24}}>
                        <Card type="inner" title={<span>按天气统计{renderMaxBtn('weather')}</span>} className="inner-card">
                            <Tabs defaultActiveKey="1" items={weatherItems} />
                        </Card>
                    </Col>
                    <Col xs={24} md={8} style={{marginBottom: 24}}>
                        <Card type="inner" title={<span>按路面性质统计{renderMaxBtn('road')}</span>} className="inner-card">
                            <Tabs defaultActiveKey="1" items={roadSurfaceItems} />
                        </Card>
                    </Col>
                    <Col xs={24} md={8} style={{marginBottom: 24}}>
                        <Card type="inner" title={<span>勘查员Top 10{renderMaxBtn('investigator')}</span>} className="inner-card">
                            <Tabs defaultActiveKey="1" items={investigatorsItems} />
                        </Card>
                    </Col>
                    <Col xs={24} md={8} style={{marginBottom: 24}}>
                        <Card type="inner" title={<span>绘图员Top 10{renderMaxBtn('drawer')}</span>} className="inner-card">
                            <Tabs defaultActiveKey="1" items={drawersItems} />
                        </Card>
                    </Col>
                </Row>
                )}
            </Card>
        </div>
    );
};

export default Statistics;
